import React, { useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  MAX_IMPORT_BYTES,
  catalogFilename,
  downloadJsonFile,
  fetchCatalogExport,
  formatImportResult,
  importCatalog,
  summarizeCatalog,
} from '../api/catalog';
import { errorMessage } from '../api/client';
import type { CatalogExport, CatalogImportStrategy, CatalogSummary } from '../types';
import { ConfirmDialog } from './ConfirmDialog';

type PendingImport = {
  payload: CatalogExport;
  summary: CatalogSummary;
  filename: string;
};

interface AdminExportImportBarProps {
  onImported?: () => void | Promise<void>;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsText(file);
  });
}

export function AdminExportImportBar({ onImported }: AdminExportImportBarProps = {}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const { fetchCharacters, fetchItems, fetchSpells, setToastMessage } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingImport | null>(null);
  const [strategy, setStrategy] = useState<CatalogImportStrategy>('merge');

  const toast = (message: string) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(null), 4000);
  };

  const handleExport = async () => {
    setError(null);
    setBusy(true);
    try {
      const data = await fetchCatalogExport();
      downloadJsonFile(data, catalogFilename(data.exported_at));
      toast('Katalog exportiert (Charaktere, Items, Zauber, Gegner).');
    } catch (err) {
      setError(errorMessage(err, 'Export fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    if (file.size > MAX_IMPORT_BYTES) {
      setError('Die Datei ist zu groß (max. 10 MB).');
      return;
    }
    try {
      const text = await readFileAsText(file);
      const parsed = JSON.parse(text) as unknown;
      const summary = summarizeCatalog(parsed);
      setStrategy('merge');
      setPending({
        payload: parsed as CatalogExport,
        summary,
        filename: file.name,
      });
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Ungültiges JSON.');
        return;
      }
      setError(errorMessage(err, 'Datei konnte nicht gelesen werden.'));
    }
  };

  const handleConfirmImport = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const result = await importCatalog(pending.payload, strategy);
      setPending(null);
      await Promise.all([fetchCharacters(), fetchItems(), fetchSpells()]);
      await onImported?.();
      toast(formatImportResult(result));
    } catch (err) {
      setError(errorMessage(err, 'Import fehlgeschlagen.'));
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const total = pending
    ? pending.summary.characters
      + pending.summary.items
      + pending.summary.spells
      + pending.summary.npcs
    : 0;

  return (
    <>
      <button
        type="button"
        className="medieval-button"
        onClick={() => void handleExport()}
        disabled={busy}
        title="Charakter-Vorlagen, Items, Zauber und Gegner als JSON speichern"
      >
        Export
      </button>
      <button
        type="button"
        className="medieval-button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        title="JSON-Katalog importieren"
      >
        Import
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        hidden
        aria-label="Katalog-Datei wählen"
        onChange={event => void handleFile(event)}
      />
      {error && <p className="form-error export-import-error" role="alert">{error}</p>}
      {pending && (
        <ConfirmDialog
          title="Katalog importieren?"
          message={`Datei „${pending.filename}“ enthält ${pending.summary.characters} Charakter-Vorlagen, ${pending.summary.items} Items, ${pending.summary.spells} Zauber und ${pending.summary.npcs} Gegner. Spieler-Kopien, Spielstände und bereits zugeordnete Gegner bleiben unberührt.`}
          confirmLabel={strategy === 'replace' ? 'Ersetzen' : 'Zusammenführen'}
          danger={strategy === 'replace'}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            if (!total) return;
            void handleConfirmImport();
          }}
        >
          <fieldset className="import-strategy" disabled={busy}>
            <legend>Strategie</legend>
            <label className="checkbox-label">
              <input
                type="radio"
                name="import-strategy"
                value="merge"
                checked={strategy === 'merge'}
                onChange={() => setStrategy('merge')}
              />
              Zusammenführen (gleichnamige Einträge aktualisieren, Rest behalten)
            </label>
            <label className="checkbox-label">
              <input
                type="radio"
                name="import-strategy"
                value="replace"
                checked={strategy === 'replace'}
                onChange={() => setStrategy('replace')}
              />
              Ersetzen (Einträge, die nicht in der Datei stehen, entfernen – Spieler-Inventar und Spiel-Gegner bleiben)
            </label>
          </fieldset>
          {total === 0 && (
            <p className="form-error" role="alert">Die Datei enthält keine importierbaren Einträge.</p>
          )}
        </ConfirmDialog>
      )}
    </>
  );
}
